"use client"
import { useEffect, useRef, useState } from "react"
import { cn } from "../../lib"
import { Copy, CopyCheck } from "lucide-react"
import { useChainId } from "wagmi"
import { useVaults } from "../hooks"
import { blessnet } from "../blessnet-chain-configs"

import { mainButton, secondaryButton } from "@telegram-apps/sdk-react"
import { useTgAccount, useTgContext } from "./TgRoot"
import { MIN_ETH_TO_PROCESS_AA_OPERATION } from "."
import { BLESSNET_API_URL } from "../../BLESSNET_API_URL"

interface TgGateProps {
  className?: string
}

const Checkbox = ({ checked, index }: { checked: boolean; index: number }) => (
  <div className="w-4 h-4 flex items-center justify-center p-[2px] border-2 border-white rounded-full relative">
    <div
      className={cn(
        "rounded-full",
        "bg-[#9EFF00] transition-all duration-300",
        checked ? "w-full h-full" : "w-0 h-0",
      )}
    />
    <div
      className={cn(
        "text-xs font-bold text-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
        checked ? "text-black" : "text-white",
      )}
    >
      {index}
    </div>
  </div>
)
export function TgGate({ className }: TgGateProps) {
  const {
    account,
    refetch,
    isSetup,
    isFetched,
    balance,
    isModalOpen,
    toggleModal,
  } = useTgContext()

  const { error, isLoading } = useTgAccount()
  const chainId = useChainId()
  const { data: vaultsData, error: vaultsError } = useVaults()
  const [isCopied, setIsCopied] = useState(false)
  const isTestnet = chainId !== blessnet.id
  const qP = Object.entries({
    hashedId: account?.data?.hashedId ?? "",
    chain: isTestnet ? "sepolia" : "mainnet",
    platform: "telegram",
    aaAddress: account.data?.account ?? "",
  }).reduce(
    (acc, [key, value]) => `${acc}&${key}=${encodeURIComponent(value)}`,
    "",
  )
  const url = `${BLESSNET_API_URL}/modular-permissions?${qP}`
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setIsCopied(true)
      setTimeout(() => {
        setIsCopied(false)
      }, 2000)
    } catch (err) {
      console.error(err)
    }
  }

  const offClick = useRef<() => void>(() => {})
  console.log("isLoading tg", isLoading, isSetup, isFetched)
  useEffect(() => {
    if (!document) {
      // not in a browser
      return
    }
    const body = document.body
    const prevOverflow = body.style.overflow || "initial"

    const oldColor = mainButton.textColor()
    const oldBgColor = mainButton.backgroundColor()
    let secondaryOnClick
    offClick.current?.()
    const handle = () => {
      if (isSetup || isLoading) {
        return
      }

      offClick.current = mainButton.onClick(toggleModal)
      if (isModalOpen) {
        body.style.overflow = "hidden"
        mainButton.setParams({
          isVisible: true,
          isEnabled: true,
          text: "I'll do it later",
          backgroundColor: "#ADF82A",
          textColor: "#111111",
          hasShineEffect: false,
        })
        secondaryOnClick = secondaryButton.onClick(refetch)

        return
      }
      if (!isModalOpen) {
        mainButton.setParams({
          isVisible: true,
          isEnabled: true,
          text: "Setup blessed account",
          backgroundColor: "#ADF82A",
          textColor: "#111111",
          hasShineEffect: true,
        })
        return
      }
    }

    handle()

    return () => {
      body.style.overflow = prevOverflow
      // not sure this is needed as nothing else
      // should have setParams before (maybe color, though?)
      mainButton.setParams({
        // text: oldText,
        textColor: oldColor,
        backgroundColor: oldBgColor,
        isVisible: false,
      })
      secondaryOnClick?.()
      secondaryButton.setParams({
        isVisible: false,
      })
      console.log("offClick on unmount", offClick.current)
      offClick.current?.()
    }
  }, [isModalOpen, refetch, toggleModal, isSetup, isLoading, isFetched])

  if (!isModalOpen) {
    return null
  }
  return (
    <div
      className={cn(
        "h-screen overflow-auto",
        "fixed inset-0 z-[1000] max-w-screen",
        "text-white nunito",
        "animate-in slide-in-from-bottom",
        className,
      )}
    >
      <div
        className={cn("flex flex-col items-center px-6 py-12 text-center")}
        style={{
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundImage:
            "url('https://lock.nyc3.cdn.digitaloceanspaces.com/bg-blessnet.svg'), linear-gradient(15deg, #045275 0%, #061C30 100%)",
        }}
      >
        <style jsx>{`
          @import url("https://fonts.googleapis.com/css2?family=Nunito:wght@200..1000&display=swap");
          .nunito {
            font-family: "Nunito", serif;
            font-optical-sizing: auto;
            font-weight: 400;
            font-style: normal;
          }
        `}</style>
        <img
          src="https://lock.nyc3.cdn.digitaloceanspaces.com/blessnet-logo.png"
          alt="BlessNet"
          className="w-28 h-28 mb-4"
        />

        <div className="mb-8">
          <p className="text-gray-300">To use this app, setup your</p>
          <p className="text-xl font-semibold text-[#9EFF00]">
            Blessed Account
          </p>
        </div>

        <div
          className="flex flex-col gap-4 w-full max-w-xs mb-8 items-start"
          style={{
            width: "fit-content",
          }}
        >
          <div className="flex items-center gap-3">
            <Checkbox checked={Boolean(account.data?.exists)} index={1} />
            <span className="text-white">Create an account</span>
          </div>
          <div
            className={cn(
              "flex items-center gap-3",
              vaultsError && "text-red-500",
            )}
          >
            <Checkbox
              checked={Boolean((vaultsData?.asDelegate?.length as number) > 0)}
              index={2}
            />
            <span className="text-white">Give permissions</span>
          </div>
          <div className="flex items-center gap-3">
            <Checkbox
              checked={
                (balance?.data as any)?.value > MIN_ETH_TO_PROCESS_AA_OPERATION
              }
              index={3}
            />
            <span className="text-white">Fund your account</span>
          </div>
        </div>

        <div className="mb-8">
          <p className="text-sm text-gray-300 mb-2">
            To setup your blessed account, visit:
          </p>
          {error ? (
            <div className="text-red-500">
              There was an error loading your account: {error?.message}
            </div>
          ) : (
            <div className="grid gap-2 grid-cols-[1fr_auto] text-left items-center border border-[#9EFF00] rounded max-w-[calc(100vw-1rem)] overflow-hidden">
              <div className="text-white overflow-ellipsis shrink grow-0 overflow-hidden whitespace-nowrap pl-3 py-2">
                {url.replace("https://", "")}
              </div>
              <button
                onClick={handleCopy}
                type="button"
                className="bg-[#9EFF00] px-2 py-2 border-none shrink-0 inline-block active:scale-95 transition-all duration-300"
              >
                {isCopied ? (
                  <CopyCheck className="w-4 h-4 text-black" />
                ) : (
                  <Copy className="w-4 h-4 text-black" />
                )}
              </button>
            </div>
          )}
          <p className="text-xs text-gray-300 mt-2">
            Delegate your main wallet to your telegram account to participate to
            the loomlock ecosystem
          </p>
        </div>

        {/* <div className="text-xs text-[#9EFF00] mb-4 flex items-center gap-1">
          <InfoCircleOutlined className="text-xs" />
          You can also use the blessed bot to set this up
        </div> */}
      </div>
    </div>
  )
}
